import {
  type AnalysisItem,
  SUGGESTION_SCALE,
  SUGGESTION_STATUSES,
  type SuggestionAnalysis,
  type SuggestionDetail,
  type SuggestionPilotSummary,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { FileSpreadsheet, GitMerge, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { date, plural } from '../../lib/format';
import { SUGGESTION_TONE } from '../../lib/support';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const SCORES = [1, 2, 3, 4, 5] as const;
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const monthLabel = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;
const days = (n: number) => plural(n, 'день', 'дня', 'дней');

/** Ссылка на предложение: номер и название, переносится на телефоне */
function ItemLink({ item }: { item: Pick<AnalysisItem, 'id' | 'number' | 'title'> }) {
  return (
    <Link
      to="/support/suggestions/$id"
      params={{ id: item.id }}
      className="inline-flex min-h-11 items-center self-start text-[14px] font-medium [overflow-wrap:anywhere] text-ink hover:underline md:min-h-0"
    >
      {item.number} · {item.title}
    </Link>
  );
}

/**
 * Анализ всех предложений (БЗ-32, БЗ-33) — для специалистов поддержки и владельца портала: сводка за период,
 * что ждёт решения дольше срока разбора, что делать первым с оценкой пользы и трудоёмкости, похожие предложения
 * с объединением повторов, разделы, месяцы, причины отказов, выгрузка в Excel и сводка тем силами Pilot
 */
export function SuggestionAnalysisPanel() {
  const [period, setPeriod] = useState(90);
  const q = useQuery({
    queryKey: ['support', 'analysis', period],
    queryFn: () => api<SuggestionAnalysis>('/support/suggestions/analysis', { query: { days: period } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const a = q.data;
  const t = a.totals;
  const tiles = [
    {
      label: 'Подано за период',
      value: String(t.created),
      note: `участников ${t.authors}, голосов ${t.votes}`,
    },
    {
      label: 'Ждут решения',
      value: String(t.review),
      note: t.waitingLong
        ? `дольше ${days(a.reviewDays)}: ${t.waitingLong}`
        : `срок разбора — ${days(a.reviewDays)}`,
      warn: t.waitingLong > 0,
    },
    { label: 'Взято в работу', value: String(t.accepted), note: `выпущено ${t.released}` },
    { label: 'Отклонено', value: String(t.rejected), note: `объединено повторов ${t.duplicates}` },
    {
      label: 'Доля принятых',
      value: t.acceptedPercent === null ? 'нет решений' : `${t.acceptedPercent}%`,
      note: 'из решённых за период',
    },
    {
      label: 'До решения, в среднем',
      value:
        t.avgDecisionDays === null ? 'нет решений' : `${String(t.avgDecisionDays).replace('.', ',')} дн.`,
      note: 'от подачи до первого статуса',
    },
    { label: 'Открыто сейчас', value: String(t.open), note: `без оценки ${t.unassessed}` },
  ];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full max-w-[220px]">
          <Select
            className="h-11 md:h-10"
            aria-label="Период анализа"
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
          >
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
            <option value={180}>Полгода</option>
            <option value={365}>Год</option>
          </Select>
        </div>
        <a
          href={`/api/v1/support/suggestions/analysis.xlsx?days=${period}`}
          className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline md:h-10"
        >
          <FileSpreadsheet className="size-4" aria-hidden />
          Выгрузить в Excel
        </a>
        <span className="text-[13px] text-ink-2">
          {date(a.from)} — {date(a.to)}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        {tiles.map((x) => (
          <div
            key={x.label}
            className={
              x.warn
                ? 'rounded-lg border border-warning/50 bg-warning-soft p-3'
                : 'rounded-lg border border-line bg-surface p-3'
            }
          >
            <div className="text-[12px] text-ink-2">{x.label}</div>
            <div className="num mt-1 text-[18px] leading-6 font-semibold">{x.value}</div>
            <div className={x.warn ? 'mt-0.5 text-[12px] text-warning' : 'mt-0.5 text-[12px] text-ink-2'}>
              {x.note}
            </div>
          </div>
        ))}
      </div>
      {a.waiting.length ? (
        <Panel
          title={`Ждут решения дольше ${days(a.reviewDays)}`}
          meta="Разберите к ближайшему показу сделанного: в план или отказ с причиной"
          bodyClassName="p-0"
        >
          <ul className="divide-y divide-line">
            {a.waiting.map((x) => (
              <li key={x.id} className="flex flex-col gap-1 px-5 py-3">
                <ItemLink item={x} />
                <div className="text-[12px] text-ink-2">
                  {x.section} · {x.authorName} · ждёт {days(x.ageDays)} · голосов {x.votes}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <Priorities items={a.priorities} unassessed={t.unassessed} />
          <Duplicates groups={a.duplicates} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <PilotSummary available={a.pilot.available} open={t.open} />
          <Panel title="По разделам портала" meta="Поданные за период" bodyClassName="p-0">
            {a.bySection.length === 0 ? (
              <p className="px-5 py-4 text-[14px] text-ink-2">За период предложений нет.</p>
            ) : (
              <ul className="divide-y divide-line">
                {a.bySection.map((x) => (
                  <li
                    key={x.section}
                    className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-2.5"
                  >
                    <span className="min-w-0 text-[14px] [overflow-wrap:anywhere]">{x.section}</span>
                    <span className="num text-[13px] text-ink-2">
                      подано {x.total} · открыто {x.open} · выпущено {x.released} · отклонено {x.rejected} ·
                      голосов {x.votes}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <Panel title="По месяцам" bodyClassName="p-0">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="border-b border-line text-left text-[12px] text-ink-2">
                  <th className="px-5 py-2 font-medium">Месяц</th>
                  <th className="px-2 py-2 text-right font-medium">Подано</th>
                  <th className="px-2 py-2 text-right font-medium">Решено</th>
                  <th className="py-2 pr-5 pl-2 text-right font-medium">Выпущено</th>
                </tr>
              </thead>
              <tbody className="num">
                {a.byMonth.map((m) => (
                  <tr key={m.month} className="border-b border-line last:border-b-0">
                    <td className="px-5 py-2">{monthLabel(m.month)}</td>
                    <td className="px-2 py-2 text-right">{m.created}</td>
                    <td className="px-2 py-2 text-right">{m.decided}</td>
                    <td className="py-2 pr-5 pl-2 text-right">{m.released}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          <Panel title="Причины отказов" meta="За период" bodyClassName="p-0">
            {a.rejections.length === 0 ? (
              <p className="px-5 py-4 text-[14px] text-ink-2">Отказов за период нет.</p>
            ) : (
              <ul className="divide-y divide-line">
                {a.rejections.map((x) => (
                  <li key={x.id} className="flex flex-col gap-1 px-5 py-3">
                    <ItemLink item={x} />
                    <p className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                      {date(x.at)} · {x.reason || 'причина не указана'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

/** Что делать первым: открытые предложения по приоритету, оценка пользы и трудоёмкости прямо в списке (БЗ-33) */
function Priorities({ items, unassessed }: { items: AnalysisItem[]; unassessed: number }) {
  return (
    <Panel
      title="Что делать первым"
      meta={unassessed ? `без оценки: ${unassessed}` : 'все открытые оценены'}
      bodyClassName="p-0"
    >
      <p className="border-b border-line px-5 py-3 text-[13px] text-ink-2">
        Приоритет = 2 × польза − трудоёмкость + голоса (не больше 10). Без оценки польза и трудоёмкость
        считаются средними. Оценку видят только специалисты поддержки.
      </p>
      {items.length === 0 ? (
        <EmptyState title="Открытых предложений нет" />
      ) : (
        <ul className="divide-y divide-line">
          {items.map((x) => (
            // Оценка в ключе: после сохранения здесь или в карточке строка начинает с того, что в базе
            <PriorityRow key={`${x.id}:${x.benefit ?? ''}:${x.effort ?? ''}`} item={x} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function PriorityRow({ item }: { item: AnalysisItem }) {
  const qc = useQueryClient();
  const [benefit, setBenefit] = useState<number | ''>(item.benefit ?? '');
  const [effort, setEffort] = useState<number | ''>(item.effort ?? '');
  const save = useMutation({
    mutationFn: (v: { benefit: number; effort: number }) =>
      api<SuggestionDetail>(`/support/suggestions/${item.id}/assessment`, { method: 'PUT', body: v }),
    onSuccess: () => {
      toast(`Оценка ${item.number} сохранена`);
      void qc.invalidateQueries({ queryKey: ['support'] });
    },
    onError: (e) => {
      // Не сохранилось — в списке снова то, что в базе, а не выбранное
      setBenefit(item.benefit ?? '');
      setEffort(item.effort ?? '');
      toast(e instanceof ApiError ? e.message : 'Не удалось сохранить оценку', 'danger');
    },
  });
  const pick = (b: number | '', e: number | '') => {
    setBenefit(b);
    setEffort(e);
    if (b !== '' && e !== '' && (b !== item.benefit || e !== item.effort))
      save.mutate({ benefit: b, effort: e });
  };
  return (
    <li className="flex flex-col gap-2 px-5 py-3">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="ink" className="num">
            приоритет {item.priority}
          </Badge>
          <Badge tone={SUGGESTION_TONE[item.status]}>{SUGGESTION_STATUSES[item.status]}</Badge>
        </div>
        <ItemLink item={item} />
        <div className="text-[12px] text-ink-2">
          {item.section} · {item.authorName} · голосов {item.votes}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="w-full sm:w-[262px]">
          <Select
            className="h-11 md:h-9"
            aria-label={`Польза ${item.number}`}
            value={benefit}
            disabled={save.isPending}
            onChange={(e) => pick(e.target.value ? Number(e.target.value) : '', effort)}
          >
            <option value="">Польза</option>
            {SCORES.map((n) => (
              <option key={n} value={n}>
                Польза {n} — {SUGGESTION_SCALE[n]}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-full sm:w-[262px]">
          <Select
            className="h-11 md:h-9"
            aria-label={`Трудоёмкость ${item.number}`}
            value={effort}
            disabled={save.isPending}
            onChange={(e) => pick(benefit, e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Трудоёмкость</option>
            {SCORES.map((n) => (
              <option key={n} value={n}>
                Трудоёмкость {n} — {SUGGESTION_SCALE[n]}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </li>
  );
}

/** Похожие открытые предложения: объединить повтор с основным — голоса перейдут к основному (БЗ-32) */
function Duplicates({ groups }: { groups: Array<{ items: AnalysisItem[] }> }) {
  const qc = useQueryClient();
  const [merge, setMerge] = useState<{ dup: AnalysisItem; main: AnalysisItem } | null>(null);
  const run = useMutation({
    mutationFn: (m: { dup: AnalysisItem; main: AnalysisItem }) =>
      api<SuggestionDetail>(`/support/suggestions/${m.dup.id}/duplicate`, {
        method: 'POST',
        body: { of: m.main.id },
      }),
    onSuccess: (_d, m) => {
      toast(`${m.dup.number} объединено с ${m.main.number}: голоса перенесены, автор получит уведомление`);
      setMerge(null);
      void qc.invalidateQueries({ queryKey: ['support'] });
      void qc.invalidateQueries({ queryKey: ['inbox'] });
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не удалось объединить', 'danger'),
  });
  return (
    <Panel
      title="Похожие предложения"
      meta="Возможные повторы: по общим словам в названии, странице и описанию"
      bodyClassName="p-0"
    >
      {groups.length === 0 ? (
        <p className="px-5 py-4 text-[14px] text-ink-2">Похожих открытых предложений нет.</p>
      ) : (
        <ul className="divide-y divide-line">
          {groups.map((g) => {
            const main = g.items[0]!;
            return (
              <li key={main.id} className="flex flex-col gap-2 px-5 py-3">
                {g.items.map((x, i) => (
                  <div
                    key={x.id}
                    className="flex flex-col gap-1.5 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <ItemLink item={x} />
                      <span className="text-[12px] text-ink-2">
                        {i === 0 ? 'основное · ' : ''}
                        {x.authorName} · голосов {x.votes} · {SUGGESTION_STATUSES[x.status].toLowerCase()}
                      </span>
                    </div>
                    {i > 0 ? (
                      <Button
                        size="sm"
                        icon={<GitMerge className="size-4" aria-hidden />}
                        onClick={() => setMerge({ dup: x, main })}
                      >
                        Повтор {main.number}
                      </Button>
                    ) : null}
                  </div>
                ))}
              </li>
            );
          })}
        </ul>
      )}
      <Dialog
        open={Boolean(merge)}
        onOpenChange={(v) => (v ? null : setMerge(null))}
        title="Объединить повтор"
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setMerge(null)}>
              Отмена
            </Button>
            <Button variant="primary" loading={run.isPending} onClick={() => merge && run.mutate(merge)}>
              Объединить
            </Button>
          </>
        }
      >
        {merge ? (
          <p className="text-[14px] leading-6">
            {merge.dup.number} «{merge.dup.title}» станет повтором {merge.main.number} «{merge.main.title}»:
            оно будет отклонено со ссылкой на основное, голоса и голос автора перейдут к основному, автор
            получит уведомление. Баллов за повтор не начисляется.
          </p>
        ) : null}
      </Dialog>
    </Panel>
  );
}

/** Сводка тем силами Pilot: темы, повторы, что сделать первым; ответ помечен «Подготовлено ИИ» */
function PilotSummary({ available, open }: { available: boolean; open: number }) {
  const ask = useMutation({
    mutationFn: () => api<SuggestionPilotSummary>('/support/suggestions/analysis/pilot', { method: 'POST' }),
  });
  return (
    <Panel
      title="Сводка Pilot"
      meta={available ? `открытых предложений: ${open}` : 'после подключения Pilot'}
      actions={
        available ? (
          <Button
            size="sm"
            icon={<Sparkles className="size-4" aria-hidden />}
            loading={ask.isPending}
            disabled={!open}
            onClick={() => ask.mutate()}
          >
            Разобрать по темам
          </Button>
        ) : null
      }
    >
      {!available ? (
        <p className="text-[14px] text-ink-2">
          Pilot сгруппирует открытые предложения по темам, найдёт повторы и подскажет, что сделать первым, —
          когда модель пройдёт контрольную проверку. Пока — списки слева и выгрузка в Excel.
        </p>
      ) : ask.isError ? (
        <ErrorBox error={ask.error} />
      ) : ask.data ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="accent">{ask.data.label}</Badge>
            <span className="text-[12px] text-ink-2">
              {ask.data.model} · предложений {ask.data.count}
            </span>
          </div>
          <p className="text-[14px] leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap">
            {ask.data.text}
          </p>
        </div>
      ) : (
        <p className="text-[14px] text-ink-2">
          Pilot прочитает открытые предложения с голосами и оценкой и предложит темы и порядок работ. Решение
          — за вами.
        </p>
      )}
    </Panel>
  );
}
